/**
 * Onboarding API client — FE-W-02a.
 *
 * 6 client function (BE-W-02..05 endpoint 1:1 매핑):
 *   - startOnboarding({ companyName }) → POST /onboarding/start
 *   - setDomain({ domain }) → POST /onboarding/domain
 *   - checkDomainAvailable(domain, signal?) → GET /onboarding/domain/available?d=
 *   - verifyDomain({ method, otp? }) → POST /onboarding/verify
 *   - finalize({ email, password, displayName? }) → POST /onboarding/admin
 *   - restoreSession() → GET /onboarding/session
 *
 * Bare axios 강제 (Auditor A-4 + pattern-axios-single-flight-refresh):
 *   - apiClient interceptor 의 401 자동 refresh → onboarding 의 401 FINGERPRINT_MISMATCH
 *     를 인증 만료로 오인 → /auth/refresh 무한 루프 → onboarding session 손실.
 *   - 본 module 은 apiClient import 금지. ESLint rule (no-restricted-imports) 로 정적 차단.
 *
 * Cookie 전송:
 *   - withCredentials: true — __Host-onboarding_sid cookie 자동 첨부.
 */

// eslint-disable-next-line no-restricted-imports
import axios, { AxiosError } from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const onboardingClient = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15_000,
});

// ============================================================
// Types
// ============================================================

export interface StartPayload {
  companyName: string;
}

export interface StartResponse {
  sessionId: string;
  currentStep: 1;
  expiresAt: string;
  companyName: string;
  domain: string | null;
}

export interface SetDomainPayload {
  domain: string;
}

export interface SetDomainResponse {
  currentStep: 2;
  institution: { id: string; domain: string; status: string };
  dnsChallenge: { recordType: 'TXT'; recordValue: string };
}

export interface DomainAvailabilityResponse {
  available: boolean;
  reason: 'available' | 'invalid_format' | 'reserved_keyword' | 'taken_active' | 'taken_pending';
  message?: string;
}

export type VerifyMethod = 'dns_txt' | 'mx_otp';

export interface VerifyPayload {
  method: VerifyMethod;
  otp?: string;
}

export interface VerifySuccessResponse {
  currentStep: 3;
  method: VerifyMethod;
}

export interface VerifyOtpPendingResponse {
  status: 'otp_sent';
  method: 'mx_otp';
  devBypassOtp?: string;
}

export interface FinalizePayload {
  email: string;
  password: string;
  displayName?: string;
}

export interface FinalizeResponse {
  // Cycle 72 — 승인 워크플로우: finalize 는 토큰을 발급하지 않고 'pending_approval' 응답.
  // 가입자는 SYSTEM_ADMIN 승인 후 정식 로그인. status 로 Step4 가 분기.
  status: 'pending_approval';
  user: { id: string; email: string; role: 'ADMIN'; institutionId: string };
  message?: string;
  // grace_until 은 승인 시점에 시작 → finalize 응답에서 제거(승인 후 /users/me 등에서 노출).
}

export interface RestoreSessionResponse {
  sessionId: string;
  currentStep: 1 | 2 | 3 | 4;
  companyName: string | null;
  domain: string | null;
  verificationMethod: 'txt' | 'mx_otp' | null;
  institutionId: string | null;
  expiresAt: string;
}

// ============================================================
// Error normalization
// ============================================================

export interface OnboardingApiError {
  code: string;
  message: string;
  currentStep?: 1 | 2 | 3 | 4;
  matched?: number;
  total?: number;
  remainingAttempts?: number;
  retryable?: boolean;
}

export class OnboardingError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: OnboardingApiError,
  ) {
    super(payload.message);
    this.name = 'OnboardingError';
  }
}

function normalize(err: unknown): never {
  if (err instanceof AxiosError && err.response) {
    const data = err.response.data as OnboardingApiError | undefined;
    if (data && typeof data === 'object' && 'code' in data) {
      throw new OnboardingError(err.response.status, data);
    }
  }
  throw err;
}

// ============================================================
// API methods
// ============================================================

export async function startOnboarding(payload: StartPayload): Promise<StartResponse> {
  try {
    const { data } = await onboardingClient.post<StartResponse>('/onboarding/start', payload);
    return data;
  } catch (err) {
    normalize(err);
  }
}

export async function setDomain(payload: SetDomainPayload): Promise<SetDomainResponse> {
  try {
    const { data } = await onboardingClient.post<SetDomainResponse>('/onboarding/domain', payload);
    return data;
  } catch (err) {
    normalize(err);
  }
}

export async function checkDomainAvailable(
  domain: string,
  signal?: AbortSignal,
): Promise<DomainAvailabilityResponse> {
  try {
    const { data } = await onboardingClient.get<DomainAvailabilityResponse>(
      '/onboarding/domain/available',
      { params: { d: domain }, signal },
    );
    return data;
  } catch (err) {
    normalize(err);
  }
}

export async function verifyDomain(
  payload: VerifyPayload,
): Promise<VerifySuccessResponse | VerifyOtpPendingResponse> {
  try {
    const { data } = await onboardingClient.post<VerifySuccessResponse | VerifyOtpPendingResponse>(
      '/onboarding/verify',
      payload,
    );
    return data;
  } catch (err) {
    normalize(err);
  }
}

export async function finalize(payload: FinalizePayload): Promise<FinalizeResponse> {
  try {
    const { data } = await onboardingClient.post<FinalizeResponse>('/onboarding/admin', payload);
    return data;
  } catch (err) {
    normalize(err);
  }
}

export async function restoreSession(): Promise<RestoreSessionResponse | null> {
  try {
    const res = await onboardingClient.get<RestoreSessionResponse>('/onboarding/session', {
      // 204 No Content (cookie 없음) — 정상 흐름. axios 가 throw 하지 않음.
      validateStatus: (s) => s === 200 || s === 204,
    });
    if (res.status === 204) return null;
    return res.data;
  } catch (err) {
    normalize(err);
  }
}
